import { z } from 'zod';

// Extend as new roles appear in the backend.
enum JwtRole {
  TenantAdmin = 'TenantAdmin',
}

// Extend as new feature flags are issued in tokens.
enum JwtFeature {
  Activity = 'Activity',
  Forecast = 'Forecast',
  Improvements = 'Improvements',
  Internal = 'Internal',
  Realtime = 'Realtime',
  // Documentation marker for routes that need a real backend feature flag
  // assigned later. Tagged routes are NOT gated — see route guards.
  Unknown = 'Unknown',
}

const knownFeatures = new Set<string>(Object.values(JwtFeature));

// Open schema — unknown claims pass through; known claims are typed.
const jwtPayloadSchema = z.object({
  tenant_id: z.string().optional(),
  role: z.enum(JwtRole).optional(),
  // Feature arrives as an array from Identity Server; unknown values are silently dropped
  // so a new backend feature doesn't break the parse before the client adds it to the enum.
  feature: z.preprocess(
    (v) =>
      (Array.isArray(v) ? v : typeof v === 'string' ? [v] : []).filter(
        (s) => typeof s === 'string' && knownFeatures.has(s),
      ),
    z.array(z.enum(JwtFeature)).optional(),
  ),
});

type JwtPayload = z.infer<typeof jwtPayloadSchema>;

function parseJwt(token: string): JwtPayload | undefined {
  const base64Url = token.split('.')[1];
  if (!base64Url) return;
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(''),
  );

  const parsed = jwtPayloadSchema.safeParse(JSON.parse(jsonPayload));
  if (!parsed.success) throw parsed.error;
  return parsed.data;
}

export { JwtFeature, JwtRole, parseJwt };
export type { JwtPayload };
